import {
  SET_BASE64_METADATA,
  SET_INTENT_UUID,
  SET_IS_TEST_CONFIGURATION_MODAL_OPEN,
  SET_TEST_COMPANY_MODEL_CONFIGURATION,
  TRIGGER_SUBMIT_CONFIGURATION,
  SET_SELECTED_CONFIGURATION_ANSWERS,
  SET_CLEARED_EXTRA_PRICE_QUESTION_SLUGS,
  SET_TRIGGER_SHOW_SUBMIT_CONFIRMED_MODAL,
  SET_SUBMISSION_IS_SLOW,
  SET_SUBMISSION_BEING_CHECKED,
  SET_FIXED_AMOUNT_CURRENCY,
  SET_INDICATIVE_QUESTION_KEYS,
  TRIGGER_SAVE_TEMPLATE,
  SET_TEMPLATE_TO_APPLY,
  SET_SELECTED_TEMPLATE,
} from 'store/constants';
import { ITestCompanyModelConfiguration } from 'types/Configuration.types';
import { ISelectedConfigurationAnswer } from 'types/ConfigurationQuestion.types';
import {
  ISubmissionBeingChecked,
  ITemplateSaveRequest,
} from '../reducers/submitConfiguration';
import { ISelectedTemplate } from 'types/Template.types';

export const setIntentUUID = (uuid: string) => ({
  type: SET_INTENT_UUID,
  payload: uuid,
});

export const triggerSubmitConfiguration = (trigger: boolean) => ({
  type: TRIGGER_SUBMIT_CONFIGURATION,
  payload: trigger,
});

export const setTestCompanyModelConfiguration = (
  testCompanyModelConfiguration: ITestCompanyModelConfiguration | null
) => ({
  type: SET_TEST_COMPANY_MODEL_CONFIGURATION,
  payload: testCompanyModelConfiguration,
});

export const setBase64Metadata = (base64Metadata: string | null) => ({
  type: SET_BASE64_METADATA,
  payload: base64Metadata,
});

export const setIsTestConfigurationModalOpen = (
  isTestConfigurationModalOpen: boolean
) => ({
  type: SET_IS_TEST_CONFIGURATION_MODAL_OPEN,
  payload: isTestConfigurationModalOpen,
});

export const setSelectedConfigurationAnswers = (
  selectedConfigurationAnswers: ISelectedConfigurationAnswer[]
) => ({
  type: SET_SELECTED_CONFIGURATION_ANSWERS,
  payload: selectedConfigurationAnswers,
});

// Slugs of extra price questions the customer explicitly cleared. Used to skip
// re-applying the default option for those questions on subsequent queries
// (mirrors manuallyClearedQuestionNames for regular questions).
export const setClearedExtraPriceQuestionSlugs = (
  clearedExtraPriceQuestionSlugs: string[]
) => ({
  type: SET_CLEARED_EXTRA_PRICE_QUESTION_SLUGS,
  payload: clearedExtraPriceQuestionSlugs,
});

export const setTriggerShowSubmitConfirmedModal = (
  triggerShowSubmitConfirmedModal: boolean
) => ({
  type: SET_TRIGGER_SHOW_SUBMIT_CONFIRMED_MODAL,
  payload: triggerShowSubmitConfirmedModal,
});

// The submission has been on its way for a minute: the overlay says it can take
// a few minutes.
export const setSubmissionIsSlow = (submissionIsSlow: boolean) => ({
  type: SET_SUBMISSION_IS_SLOW,
  payload: submissionIsSlow,
});

// Logyx core could not tell whether the submission reached the supplier, and is
// checking it; Logyx core's own text, when it gave one.
export const setSubmissionBeingChecked = (
  submissionBeingChecked: ISubmissionBeingChecked | null
) => ({
  type: SET_SUBMISSION_BEING_CHECKED,
  payload: submissionBeingChecked,
});

export const setFixedAmountCurrency = (fixedAmountCurrency: string) => ({
  type: SET_FIXED_AMOUNT_CURRENCY,
  payload: fixedAmountCurrency,
});

// initialKeys of questions the user marked as "indicative" (Nader te bepalen) in
// quotation mode. Kept keyed by initialKey so the marks survive the per-query
// setQuestions re-render.
export const setIndicativeQuestionKeys = (
  indicativeQuestionKeys: string[]
) => ({
  type: SET_INDICATIVE_QUESTION_KEYS,
  payload: indicativeQuestionKeys,
});

// Fired by SubHeader when the user saves a configuration as a template; consumed
// by useListenForSaveTemplateTrigger inside QueryProvider, which is where the
// freshest query payload lives. Pass null to clear.
export const triggerSaveTemplate = (
  templateSaveRequest: ITemplateSaveRequest | null
) => ({
  type: TRIGGER_SAVE_TEMPLATE,
  payload: templateSaveRequest,
});

// Picked a template in the quotation configurator. Consumed by the apply effect
// in QueryProvider, which clears it once the form has been loaded from it.
export const setTemplateToApply = (templateId: number | null) => ({
  type: SET_TEMPLATE_TO_APPLY,
  payload: templateId,
});

// What the template picker shows. Set when a template is applied, cleared by
// "Clear all fields" and whenever the model changes.
export const setSelectedTemplate = (
  selectedTemplate: ISelectedTemplate | null
) => ({
  type: SET_SELECTED_TEMPLATE,
  payload: selectedTemplate,
});
